// Cross-page handoff for "this listing's server-side state almost certainly
// changed while the user was away, so refetch it on arrival."
//
// The listing detail query carries a 30s `staleTime` (see useListingDetail),
// which is what we want for ordinary feed → detail → back → detail churn. It
// is exactly wrong for the checkout round-trip: reserving holds a portion and
// abandoning releases it, so a user who bails out of checkout and lands back
// on the detail page inside that 30s window is shown the held count. The
// detail page marks the listing before it hands off to checkout and consumes
// the mark when it remounts, forcing one refetch past `staleTime`.
//
// This is a belt-and-braces companion to the cancel mutation's own
// invalidation, not a replacement for it: the mark also covers the paths where
// no cancel fires at all (a completed purchase, a hold the server expired on
// its own, a checkout tab that was reloaded).
//
// sessionStorage (not in-memory) so it survives a full reload inside checkout,
// which is one of the ways a hold gets released behind the user's back.

const KEY_PREFIX = 'listing-stale:';

export function markListingStale(listingId: string) {
  try {
    sessionStorage.setItem(KEY_PREFIX + listingId, '1');
  } catch {
    // sessionStorage can be unavailable in private-browsing edge cases. A
    // missed refetch degrades to the old stale-for-30s behavior rather than
    // breaking the order flow, so swallow it.
  }
}

/** Returns true and clears the mark if this listing needs a forced refetch. */
export function consumeListingStale(listingId: string): boolean {
  try {
    const key = KEY_PREFIX + listingId;
    if (sessionStorage.getItem(key)) {
      sessionStorage.removeItem(key);
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}
